import { useNavigate, useLocation } from '@tanstack/react-router';
import { useEffect, type ReactNode } from 'react';
import { useAuth } from '@/lib/auth/store';

/**
 * Auth gate. Wraps routes that require a firm-team bearer token. When the
 * store is empty we send the user to `/login`, preserving the original
 * destination (pathname only) so the login flow can bounce back after a
 * successful authentication.
 *
 * The redirect fires from an effect exactly once per (token, path) change —
 * NOT from a render-time `<Navigate>` — so it can't thrash. And `next` carries
 * only `location.pathname` (never the current search string), so a redirect
 * can never nest a previous `next` into a new one and grow the URL without
 * bound (which previously produced a redirect loop + HTTP 414).
 */
export function RequireAuth({ children }: { children: ReactNode }) {
  const token = useAuth((s) => s.token);
  const navigate = useNavigate();
  const { pathname } = useLocation();

  useEffect(() => {
    // Guard against re-firing once the path has become /login (RequireAuth is
    // briefly still mounted during the redirect transition).
    if (!token && pathname !== '/login') {
      void navigate({ to: '/login', search: { next: pathname }, replace: true });
    }
  }, [token, pathname, navigate]);

  if (!token) return null;
  return <>{children}</>;
}
